/**
 * Company model render surface — read-only manifest consumer (Seq 6).
 * Routes: /render (selected company) · /render/:workspaceSlug (deep link)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Spin, Typography } from 'antd';
import { RenderManifestView } from './RenderManifestView';
import {
  fetchRenderManifestWithMeta,
  isKryspaceRenderServiceError,
} from '../../services/kryspaceRenderService';
import type { KryspaceRenderManifest } from '../../types/kryspaceRenderManifest';
import { RENDER_SURFACE_DEV_MOCK } from '../../config';
import { useUser } from '../../contexts/UserContext';
import { SELECTED_COMPANY_KRYSPACE_SLUG_KEY } from '../../config/kryspaceReadSource';
import { resolveRenderSurfaceSlugSession } from '../../utils/renderSurfaceSlugSession';
import { useCanonicalRouteOnCompanySwitch } from '../../hooks/useCanonicalRouteOnCompanySwitch';
import { usePublishSurfaceChatContext } from '../../hooks/usePublishSurfaceChatContext';
import { RenderSurfaceBindingSubtitle } from './RenderSurfaceBindingSubtitle';
import { companySelectionAlertDescription } from '../../utils/companySelectionHelp';
import pageStyles from './RenderSurface.module.css';

const { Paragraph } = Typography;

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; manifest: KryspaceRenderManifest; devMock: boolean }
  | { status: 'error'; message: string; code?: string; httpStatus?: number };

export const CompanyModelRenderSurfacePage: React.FC = () => {
  const { workspaceSlug: routeSlug } = useParams<{ workspaceSlug?: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading: userLoading,
    selectedCompany,
    availableCompanies,
    loadUserCompanies,
  } = useUser();
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>({ status: 'idle' });

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    void (async () => {
      setCompaniesLoaded(false);
      try {
        await loadUserCompanies();
      } finally {
        setCompaniesLoaded(true);
      }
    })();
  }, [user?.id, isAuthenticated, loadUserCompanies]);

  const slugSession = useMemo(
    () =>
      resolveRenderSurfaceSlugSession({
        routeSlug,
        selectedCompany,
        availableCompanies,
        userCompanyId: user?.companyId,
        persistedCompanyId:
          typeof localStorage !== 'undefined' ? localStorage.getItem('selectedCompanyId') : null,
        persistedKryspaceSlug:
          typeof localStorage !== 'undefined'
            ? localStorage.getItem(SELECTED_COMPANY_KRYSPACE_SLUG_KEY)
            : null,
      }),
    [routeSlug, selectedCompany, availableCompanies, user?.companyId]
  );

  const {
    workspaceCompanyId,
    companyRecord,
    derivedSlug,
    slugFromRoute,
    effectiveSlug,
    requiresCompanyContext,
  } = slugSession;

  useCanonicalRouteOnCompanySwitch(slugFromRoute, selectedCompany?.id, '/render', navigate);

  const load = useCallback(async () => {
    if (!effectiveSlug) {
      setLoadState({ status: 'idle' });
      return;
    }
    setLoadState({ status: 'loading' });
    try {
      const { manifest, devMock } = await fetchRenderManifestWithMeta(effectiveSlug);
      setLoadState({ status: 'ready', manifest, devMock });
    } catch (err) {
      const message = isKryspaceRenderServiceError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Failed to load render surface';
      const code = isKryspaceRenderServiceError(err) ? err.code : undefined;
      const httpStatus = isKryspaceRenderServiceError(err) ? err.httpStatus : undefined;
      setLoadState({ status: 'error', message, code, httpStatus });
    }
  }, [effectiveSlug]);

  useEffect(() => {
    void load();
  }, [load, selectedCompany?.id, selectedCompany?.kryspace_slug]);

  const companyLabel = useMemo(
    () =>
      selectedCompany?.name ??
      companyRecord?.name ??
      (slugFromRoute ? 'Deep link' : workspaceCompanyId ?? 'Not selected'),
    [selectedCompany?.name, companyRecord?.name, slugFromRoute, workspaceCompanyId]
  );

  const renderSurfaceContext = useMemo(() => {
    if (!effectiveSlug) return null;
    const manifestTitle =
      loadState.status === 'ready' ? loadState.manifest.title ?? effectiveSlug : undefined;
    return {
      surfaceId: `render:${effectiveSlug}`,
      surfaceLabel: `${companyLabel} · Render surface`,
      route: pathname,
      summary: manifestTitle ?? `Workspace ${effectiveSlug}`,
      metadata: {
        workspaceSlug: effectiveSlug,
        manifestLoaded: loadState.status === 'ready',
      },
    };
  }, [companyLabel, effectiveSlug, loadState, pathname]);

  usePublishSurfaceChatContext(renderSurfaceContext);

  if (userLoading || !isAuthenticated) {
    return (
      <div className={pageStyles.companyModelRenderPage} style={{ textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (requiresCompanyContext && !workspaceCompanyId) {
    if (!companiesLoaded) {
      return (
        <div className={pageStyles.companyModelRenderPage} style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <Paragraph type="secondary" style={{ marginTop: 16 }}>
            Loading your company…
          </Paragraph>
        </div>
      );
    }
    if (availableCompanies.length === 0) {
      return (
        <div className={pageStyles.companyModelRenderPage}>
          <Alert
            type="error"
            showIcon
            message="No companies found"
            description="We could not load any companies for your account. Select a company from the header after signing in, or contact support."
          />
          <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/dashboard')}>
            Go to dashboard
          </Button>
        </div>
      );
    }
    return (
      <div className={pageStyles.companyModelRenderPage} data-testid="render-surface-select-company">
        <Alert
          type="warning"
          showIcon
          message="Select a company"
          description={companySelectionAlertDescription({ includeCreateHint: true })}
        />
      </div>
    );
  }

  if (!effectiveSlug) {
    return (
      <div className={pageStyles.companyModelRenderPage}>
        <Alert
          type="warning"
          showIcon
          message="Workspace slug unavailable"
          description="Could not resolve a kryspace workspace slug for the selected company. Set engineering_workspace_slug on the Central supply row, or open a direct link such as /render/prime_ops."
        />
      </div>
    );
  }

  const controlBridgeLink = slugFromRoute
    ? `/render/${effectiveSlug}/control-bridge`
    : '/render/control-bridge';

  return (
    <div className={pageStyles.companyModelRenderPage} data-testid="render-surface-page">
      <header className={pageStyles.pageHeader}>
        <h1 className={pageStyles.pageTitle}>Company model render surface</h1>
        <p className={pageStyles.pageSubtitle}>
          Read-only view derived from kryspace
        </p>
        <RenderSurfaceBindingSubtitle
          className={pageStyles.pageSubtitle}
          companyLabel={companyLabel}
          effectiveSlug={effectiveSlug}
          slugFromRoute={slugFromRoute}
          slugSource={derivedSlug?.source}
          derivedSlug={derivedSlug?.slug}
        />
        <p style={{ marginTop: 8, marginBottom: 0 }}>
          <Link to={controlBridgeLink}>Open Control Bridge →</Link>
        </p>
      </header>

      {loadState.status === 'ready' && loadState.devMock ? (
        <Alert
          type="info"
          showIcon
          className={pageStyles.devMockBanner}
          message="Dev mock mode"
          description="Manifest is from REACT_APP_RENDER_SURFACE_DEV_MOCK — not the live kryspace read API. Unset for operator sign-off."
          data-testid="render-surface-dev-mock-banner"
        />
      ) : null}

      {loadState.status === 'loading' || loadState.status === 'idle' ? (
        <div className={pageStyles.loading} role="status" data-testid="render-surface-loading">
          Loading render manifest…
        </div>
      ) : null}

      {loadState.status === 'error' ? (
        <div className={pageStyles.errorPanel} role="alert" data-testid="render-surface-error">
          <p>{loadState.message}</p>
          {loadState.code === 'FORBIDDEN' || loadState.httpStatus === 403 ? (
            <p>
              You are not allowed to read workspace <code>{effectiveSlug}</code>. This is expected for
              unauthorized slugs — the app must not fall back to dev mock (KRYBOT spec §5).
            </p>
          ) : null}
          {loadState.code === 'NOT_CONFIGURED' ? (
            <p>
              Phase B: backend route{' '}
              <code>/api/v1/kryspace/workspace/&lt;slug&gt;/attention-manifest</code> (or legacy{' '}
              <code>REACT_APP_KRYSPACE_READ_API_URL</code>). For local dev only, set{' '}
              <code>REACT_APP_RENDER_SURFACE_DEV_MOCK=true</code>.
            </p>
          ) : null}
          {loadState.code === 'SERVICE_UNAVAILABLE' ? (
            <p>
              Kryspace read API returned 503. Confirm backend env (<code>KRYSPACE_WS</code>, router
              mounted) or use dev mock for UI-only work.
            </p>
          ) : null}
          {loadState.code === 'NOT_FOUND' && derivedSlug?.source === 'name_hint' ? (
            <p>
              The slug was derived from the company display name and may not match a kryspace folder.
              Pilot slugs: <code>prime_ops</code>, <code>krysalis_central</code>,{' '}
              <code>prime_ops</code>.
            </p>
          ) : null}
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : null}

      {loadState.status === 'ready' ? (
        <RenderManifestView manifest={loadState.manifest} />
      ) : null}

      {process.env.NODE_ENV === 'development' && RENDER_SURFACE_DEV_MOCK ? (
        <p className={pageStyles.devEnvHint}>
          REACT_APP_RENDER_SURFACE_DEV_MOCK is enabled in this build.
        </p>
      ) : null}
    </div>
  );
};

export default CompanyModelRenderSurfacePage;
